import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { getProfile, saveProfile, parseResume, getParseResumeStatus } from '../../services/api'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import './Profile.scss'

interface ContactInfo {
  name: string
  email: string
  address: string
  phone: string
  linkedIn: string
  github: string
  portfolio: string
}

interface Experience {
  id: number
  title: string
  company: string
  location: string
  startDate: string
  endDate: string
  current: boolean
  description: string
  points: string[]
}

interface Education {
  id: number
  school: string
  degree: string
  field: string
  startDate: string
  endDate: string
  current: boolean
  description: string
}

// Sortable Experience Item Component
interface SortableExperienceItemProps {
  experience: Experience
  index: number
  experienceErrors: Record<string, string>
  isDisabled: boolean
  updateExperience: (index: number, field: keyof Experience, value: any) => void
  updateExperienceMultiple: (index: number, updates: Partial<Experience>) => void
  removeExperience: (index: number) => void
  setExperienceErrors: React.Dispatch<React.SetStateAction<Record<number, Record<string, string>>>>
}

function SortableExperienceItem({
  experience,
  index,
  experienceErrors,
  isDisabled,
  updateExperience,
  updateExperienceMultiple,
  removeExperience,
  setExperienceErrors,
}: SortableExperienceItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: experience.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="card mb-3"
      data-experience-index={index}
    >
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-muted"
              style={{ cursor: 'grab' }}
              {...attributes}
              {...listeners}
              disabled={isDisabled}
              aria-label="Drag to reorder"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                style={{ display: 'block' }}
              >
                <path d="M10 13a1 1 0 100-2 1 1 0 000 2zm-4 0a1 1 0 100-2 1 1 0 000 2zm4-4a1 1 0 100-2 1 1 0 000 2zm-4 0a1 1 0 100-2 1 1 0 000 2zm4-4a1 1 0 100-2 1 1 0 000 2zM6 5a1 1 0 100-2 1 1 0 000 2z" />
              </svg>
            </button>
            <h5 className="card-title mb-0">Experience {index + 1}</h5>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => removeExperience(index)}
            disabled={isDisabled}
          >
            Remove
          </button>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">
              Job Title <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${experienceErrors?.title ? 'is-invalid' : ''}`}
              value={experience.title}
              onChange={(e) => updateExperience(index, 'title', e.target.value)}
              disabled={isDisabled}
              placeholder="e.g., Software Engineer"
            />
            {experienceErrors?.title && (
              <div className="invalid-feedback">{experienceErrors.title}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">
              Company <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${experienceErrors?.company ? 'is-invalid' : ''}`}
              value={experience.company}
              onChange={(e) => updateExperience(index, 'company', e.target.value)}
              disabled={isDisabled}
              placeholder="e.g., Acme Inc."
            />
            {experienceErrors?.company && (
              <div className="invalid-feedback">{experienceErrors.company}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">Location</label>
            <input
              type="text"
              className="form-control"
              value={experience.location}
              onChange={(e) => updateExperience(index, 'location', e.target.value)}
              disabled={isDisabled}
              placeholder="e.g., San Francisco, CA"
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">
              Start Date <span className="text-danger">*</span>
            </label>
            <input
              type="month"
              className={`form-control ${experienceErrors?.startDate ? 'is-invalid' : ''}`}
              value={experience.startDate}
              onChange={(e) => updateExperience(index, 'startDate', e.target.value)}
              disabled={isDisabled}
            />
            {experienceErrors?.startDate && (
              <div className="invalid-feedback">{experienceErrors.startDate}</div>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label">
              End Date {!experience.current && <span className="text-danger">*</span>}
            </label>
            <input
              type="month"
              className={`form-control ${experienceErrors?.endDate ? 'is-invalid' : ''}`}
              value={experience.endDate}
              onChange={(e) => updateExperience(index, 'endDate', e.target.value)}
              disabled={experience.current || isDisabled}
            />
            {experienceErrors?.endDate && (
              <div className="invalid-feedback">{experienceErrors.endDate}</div>
            )}
            <div className="form-check mt-2" style={{ paddingLeft: '2rem', position: 'relative', zIndex: 1 }}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`experience-current-${index}`}
                checked={experience.current}
                onChange={(e) => {
                  const newValue = e.target.checked
                  if (newValue) {
                    updateExperienceMultiple(index, { current: newValue, endDate: '' })
                    if (experienceErrors?.endDate) {
                      const newErrors = { ...experienceErrors }
                      delete newErrors.endDate
                      setExperienceErrors((prev) => {
                        const updated = { ...prev }
                        if (Object.keys(newErrors).length === 0) {
                          delete updated[index]
                        } else {
                          updated[index] = newErrors
                        }
                        return updated
                      })
                    }
                  } else {
                    updateExperience(index, 'current', newValue)
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation()
                }}
                disabled={isDisabled}
                style={{ cursor: 'pointer', position: 'relative', zIndex: 2 }}
              />
              <label
                className="form-check-label"
                htmlFor={`experience-current-${index}`}
                onClick={(e) => {
                  e.preventDefault()
                  if (!isDisabled) {
                    const newValue = !experience.current
                    if (newValue) {
                      updateExperienceMultiple(index, { current: newValue, endDate: '' })
                      if (experienceErrors?.endDate) {
                        const newErrors = { ...experienceErrors }
                        delete newErrors.endDate
                        setExperienceErrors((prev) => {
                          const updated = { ...prev }
                          if (Object.keys(newErrors).length === 0) {
                            delete updated[index]
                          } else {
                            updated[index] = newErrors
                          }
                          return updated
                        })
                      }
                    } else {
                      updateExperience(index, 'current', newValue)
                    }
                  }
                }}
                style={{ cursor: 'pointer', position: 'relative', zIndex: 2 }}
              >
                Current Job
              </label>
            </div>
          </div>
          <div className="col-12">
            <label className="form-label">
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-control ${experienceErrors?.description ? 'is-invalid' : ''}`}
              rows={3}
              value={experience.description}
              onChange={(e) => updateExperience(index, 'description', e.target.value)}
              disabled={isDisabled}
              placeholder="Describe your responsibilities and achievements..."
            />
            {experienceErrors?.description && (
              <div className="invalid-feedback">{experienceErrors.description}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Sortable Education Item Component
interface SortableEducationItemProps {
  education: Education
  index: number
  educationErrors: Record<string, string>
  isDisabled: boolean
  updateEducation: (index: number, field: keyof Education, value: any) => void
  updateEducationMultiple: (index: number, updates: Partial<Education>) => void
  removeEducation: (index: number) => void
  setEducationErrors: React.Dispatch<React.SetStateAction<Record<number, Record<string, string>>>>
}

function SortableEducationItem({
  education,
  index,
  educationErrors,
  isDisabled,
  updateEducation,
  updateEducationMultiple,
  removeEducation,
  setEducationErrors,
}: SortableEducationItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: education.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="card mb-3"
      data-education-index={index}
    >
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-muted"
              style={{ cursor: 'grab' }}
              {...attributes}
              {...listeners}
              disabled={isDisabled}
              aria-label="Drag to reorder"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                style={{ display: 'block' }}
              >
                <path d="M10 13a1 1 0 100-2 1 1 0 000 2zm-4 0a1 1 0 100-2 1 1 0 000 2zm4-4a1 1 0 100-2 1 1 0 000 2zm-4 0a1 1 0 100-2 1 1 0 000 2zm4-4a1 1 0 100-2 1 1 0 000 2zM6 5a1 1 0 100-2 1 1 0 000 2z" />
              </svg>
            </button>
            <h5 className="card-title mb-0">Education {index + 1}</h5>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => removeEducation(index)}
            disabled={isDisabled}
          >
            Remove
          </button>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">
              School <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${educationErrors?.school ? 'is-invalid' : ''}`}
              value={education.school}
              onChange={(e) => updateEducation(index, 'school', e.target.value)}
              disabled={isDisabled}
              placeholder="e.g., University of California"
            />
            {educationErrors?.school && (
              <div className="invalid-feedback">{educationErrors.school}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">
              Degree <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${educationErrors?.degree ? 'is-invalid' : ''}`}
              value={education.degree}
              onChange={(e) => updateEducation(index, 'degree', e.target.value)}
              disabled={isDisabled}
              placeholder="e.g., Bachelor of Science"
            />
            {educationErrors?.degree && (
              <div className="invalid-feedback">{educationErrors.degree}</div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">
              Field of Study <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${educationErrors?.field ? 'is-invalid' : ''}`}
              value={education.field}
              onChange={(e) => updateEducation(index, 'field', e.target.value)}
              disabled={isDisabled}
              placeholder="e.g., Computer Science"
            />
            {educationErrors?.field && (
              <div className="invalid-feedback">{educationErrors.field}</div>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label">
              Start Date <span className="text-danger">*</span>
            </label>
            <input
              type="month"
              className={`form-control ${educationErrors?.startDate ? 'is-invalid' : ''}`}
              value={education.startDate}
              onChange={(e) => updateEducation(index, 'startDate', e.target.value)}
              disabled={isDisabled}
            />
            {educationErrors?.startDate && (
              <div className="invalid-feedback">{educationErrors.startDate}</div>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label">
              End Date {!education.current && <span className="text-danger">*</span>}
            </label>
            <input
              type="month"
              className={`form-control ${educationErrors?.endDate ? 'is-invalid' : ''}`}
              value={education.endDate}
              onChange={(e) => updateEducation(index, 'endDate', e.target.value)}
              disabled={education.current || isDisabled}
            />
            {educationErrors?.endDate && (
              <div className="invalid-feedback">{educationErrors.endDate}</div>
            )}
            <div className="form-check mt-2" style={{ paddingLeft: '2rem' }}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`education-current-${index}`}
                checked={education.current}
                onChange={(e) => {
                  const newValue = e.target.checked
                  if (newValue) {
                    updateEducationMultiple(index, { current: newValue, endDate: '' })
                    if (educationErrors?.endDate) {
                      const newErrors = { ...educationErrors }
                      delete newErrors.endDate
                      setEducationErrors((prev) => {
                        const updated = { ...prev }
                        if (Object.keys(newErrors).length === 0) {
                          delete updated[index]
                        } else {
                          updated[index] = newErrors
                        }
                        return updated
                      })
                    }
                  } else {
                    updateEducation(index, 'current', newValue)
                  }
                }}
                disabled={isDisabled}
              />
              <label
                className="form-check-label"
                htmlFor={`education-current-${index}`}
                style={{ cursor: 'pointer' }}
              >
                Currently Enrolled
              </label>
            </div>
          </div>
          <div className="col-12">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={2}
              value={education.description}
              onChange={(e) => updateEducation(index, 'description', e.target.value)}
              disabled={isDisabled}
              placeholder="Relevant coursework, honors, or achievements..."
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// Sortable Skill Item Component
interface SortableSkillItemProps {
  skill: string
  index: number
  isDisabled: boolean
  removeSkill: (index: number) => void
}

function SortableSkillItem({
  skill,
  index,
  isDisabled,
  removeSkill,
}: SortableSkillItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: index })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <span
      ref={setNodeRef}
      style={style}
      className="badge bg-primary d-flex align-items-center"
    >
      <button
        type="button"
        className="btn btn-sm btn-link p-0 text-white me-1"
        style={{ cursor: 'grab', minWidth: '16px' }}
        {...attributes}
        {...listeners}
        disabled={isDisabled}
        aria-label="Drag to reorder"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="currentColor"
          style={{ display: 'block' }}
        >
          <path d="M10 13a1 1 0 100-2 1 1 0 000 2zm-4 0a1 1 0 100-2 1 1 0 000 2zm4-4a1 1 0 100-2 1 1 0 000 2zm-4 0a1 1 0 100-2 1 1 0 000 2zm4-4a1 1 0 100-2 1 1 0 000 2zM6 5a1 1 0 100-2 1 1 0 000 2z" />
        </svg>
      </button>
      {skill}
      <button
        type="button"
        className="skill-remove-btn ms-2"
        onClick={() => removeSkill(index)}
        disabled={isDisabled}
        aria-label="Remove skill"
      >
        ×
      </button>
    </span>
  )
}

/**
 * Parse date string from various formats to yyyy-MM format for HTML date inputs
 * Handles formats like: "Jan 202", "Sept 20", "March 2", "May 201", "2003", etc.
 */
function parseDateToYYYYMM(dateString: string): string {
  if (!dateString || dateString.trim() === '') {
    return ''
  }

  const trimmed = dateString.trim()
  
  // If already in yyyy-MM format, return as is
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    return trimmed
  }

  // If just a year (e.g., "2003"), default to January
  if (/^\d{4}$/.test(trimmed)) {
    return `${trimmed}-01`
  }

  // Month name mappings
  const monthMap: { [key: string]: string } = {
    'january': '01', 'jan': '01',
    'february': '02', 'feb': '02',
    'march': '03', 'mar': '03',
    'april': '04', 'apr': '04',
    'may': '05',
    'june': '06', 'jun': '06',
    'july': '07', 'jul': '07',
    'august': '08', 'aug': '08',
    'september': '09', 'sept': '09', 'sep': '09',
    'october': '10', 'oct': '10',
    'november': '11', 'nov': '11',
    'december': '12', 'dec': '12'
  }

  // Try to parse formats like "Jan 202", "Sept 20", "March 2", "May 201"
  // Pattern: month name followed by 1-4 digits
  const pattern = /^([a-z]+)\s+(\d{1,4})$/i
  const match = trimmed.match(pattern)
  
  if (match) {
    const monthName = match[1].toLowerCase()
    const yearStr = match[2]
    
    const month = monthMap[monthName]
    if (month) {
      let year = yearStr
      
      if (yearStr.length === 1) {
        // Single digit like "2" -> assume "2002"
        year = `200${yearStr}`
      } else if (yearStr.length === 2) {
        // 2-digit years: "20" -> "2020", "21" -> "2021", etc.
        year = `20${yearStr}`
      } else if (yearStr.length === 3) {
        // 3-digit years like "202" -> "2020", "201" -> "2021", "200" -> "2000"
        const yearNum = parseInt(yearStr, 10)
        if (yearNum >= 200 && yearNum < 300) {
          // "202" -> "2020", "201" -> "2021"
          year = `20${yearStr.slice(-2)}`
        } else if (yearNum >= 100 && yearNum < 200) {
          // "100" -> "2000", "199" -> "1999" (unlikely but handle it)
          year = `1${yearStr}`
        } else {
          year = yearStr
        }
      }
      // 4-digit years are already correct
      
      return `${year}-${month}`
    }
  }

  // If we can't parse it, try to extract just the year
  const yearMatch = trimmed.match(/\d{4}/)
  if (yearMatch) {
    return `${yearMatch[0]}-01`
  }

  // Return empty string if we can't parse it
  logger.warn('Could not parse date', { dateString })
  return ''
}

function Profile() {
  const { user, isLoading: authLoading, getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  
  // Test mode: provide mock user if in test mode
  const isTestMode = typeof window !== 'undefined' && 
    (localStorage.getItem('__TEST_MODE__') === 'true' || 
     window.location.search.includes('testMode=true'));
  const testUser = isTestMode ? {
    email: 'test@example.com',
    name: 'Test User',
    picture: 'https://via.placeholder.com/150',
    sub: 'auth0|test123',
  } : null;
  const effectiveUser = user || testUser;
  const effectiveAuthLoading = isTestMode ? false : authLoading;
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | false>(false)
  const [parsing, setParsing] = useState(false)
  const [parseStatus, setParseStatus] = useState<string | null>(null)
  const [showSkillInput, setShowSkillInput] = useState(false)
  const [newSkill, setNewSkill] = useState('')
  const [experienceErrors, setExperienceErrors] = useState<Record<number, Record<string, string>>>({})
  const [educationErrors, setEducationErrors] = useState<Record<number, Record<string, string>>>({})
  const [contactInfoErrors, setContactInfoErrors] = useState<Record<string, string>>({})
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const skillInputRef = useRef<HTMLInputElement>(null)
  const currentJobIdRef = useRef<string | null>(null)
  const hasLoadedProfileRef = useRef(false)
  const lastUserIdRef = useRef<string | null>(null)
  
  // Form state - structured profile data
  const [aboutMe, setAboutMe] = useState('')
  const [experiences, setExperiences] = useState<Experience[]>([])
  const [educations, setEducations] = useState<Education[]>([])
  const [skills, setSkills] = useState<string[]>([])
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    name: '',
    email: '',
    address: '',
    phone: '',
    linkedIn: '',
    github: '',
    portfolio: ''
  })

  // Helper function to process and set profile data
  const processProfileData = useCallback((profileData: any) => {
    if (!profileData) return
    
    setAboutMe(profileData.aboutMe || '')
    
    // Convert experiences - merge points into description if description is empty
    const processedExperiences = (profileData.experiences || []).map((exp: any) => {
      let description = exp.description || ''
      // If description is empty but points exist, convert points to description
      if (!description && exp.points && Array.isArray(exp.points) && exp.points.length > 0) {
        description = exp.points.map((point: string) => `• ${point}`).join('\n')
      }
      return {
        ...exp,
        description: description,
        points: exp.points || [],
        startDate: parseDateToYYYYMM(exp.startDate || ''),
        endDate: parseDateToYYYYMM(exp.endDate || '')
      }
    })
    setExperiences(processedExperiences)
    
    // Process educations - parse dates
    const processedEducations = (profileData.educations || []).map((edu: any) => ({
      ...edu,
      startDate: parseDateToYYYYMM(edu.startDate || ''),
      endDate: parseDateToYYYYMM(edu.endDate || '')
    }))
    setEducations(processedEducations)
    setSkills(profileData.skills || [])
    setContactInfo({
      name: profileData.contactInfo?.name || effectiveUser?.name || '',
      email: profileData.contactInfo?.email || '',
      address: profileData.contactInfo?.address || '',
      phone: profileData.contactInfo?.phone || '',
      linkedIn: profileData.contactInfo?.linkedIn || '',
      github: profileData.contactInfo?.github || '',
      portfolio: profileData.contactInfo?.portfolio || ''
    })
  }, [effectiveUser])

  // Helper function to load and process profile data
  const loadProfileData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true)
      }
      setError(null)
      // In test mode, use a mock token function
      const tokenFn = isTestMode 
        ? async () => 'mock-test-token'
        : getAccessTokenSilently;
      const profileData = await getProfile(tokenFn)
      processProfileData(profileData)
    } catch (err) {
      logger.error('Error loading profile', {
        error: err instanceof Error ? err.message : String(err),
      });
      const errorMessage = err instanceof Error ? err.message : String(err)
      if (!errorMessage.includes('404')) {
        Sentry.captureException(err, {
          tags: { component: 'Profile', function: 'loadProfile' },
        })
        setError(errorMessage || 'Failed to load profile')
      }
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }, [getAccessTokenSilently, processProfileData, isTestMode])

  // Load profile data on mount
  useEffect(() => {
    const currentUserId = effectiveUser?.sub || null
    
    // Reset the flag if user changed (e.g., logout/login)
    if (lastUserIdRef.current !== currentUserId) {
      hasLoadedProfileRef.current = false
      lastUserIdRef.current = currentUserId
    }

    // Prevent duplicate calls in React StrictMode or when dependencies change unnecessarily
    if (hasLoadedProfileRef.current) {
      return
    }

    if (isTestMode) {
      // In test mode, load immediately
      loadProfileData()
      hasLoadedProfileRef.current = true
    } else if (!effectiveAuthLoading && effectiveUser) {
      loadProfileData()
      hasLoadedProfileRef.current = true
    }
  }, [effectiveUser, effectiveAuthLoading, loadProfileData, isTestMode])

  // Poll for parse status
  useEffect(() => {
    if (parsing) {
      const pollStatus = async () => {
        try {
          const status = await getParseResumeStatus(getAccessTokenSilently)
          setParseStatus(status.status)
          
          if (status.status === 'completed') {
            // Only process completion if we have a jobId for the current upload
            // and it matches the completed jobId. This prevents processing stale
            // completion statuses from previous uploads.
            if (!currentJobIdRef.current) {
              return // Continue polling until we have a jobId
            }
            
            if (status.jobId !== currentJobIdRef.current) {
              return // Continue polling
            }
            setParsing(false)
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current)
              pollingIntervalRef.current = null
            }
            
            // Clear the current jobId since we've processed it
            currentJobIdRef.current = null
            
            // Always reload from API to ensure we have the latest data
            // Retry a few times with delays to handle DynamoDB eventual consistency
            let retries = 3
            let profileLoaded = false
            
            while (retries > 0 && !profileLoaded) {
              try {
                await new Promise(resolve => setTimeout(resolve, 1000)) // 1 second delay for DynamoDB consistency
                await loadProfileData(false)
                profileLoaded = true
              } catch (err) {
                logger.error('Error reloading profile', {
                  error: err instanceof Error ? err.message : String(err),
                  retriesLeft: retries,
                });
                retries--
                if (retries === 0) {
                  // If we can't reload, still show success but log the error
                  logger.error('Failed to reload profile after multiple retries')
                }
              }
            }
            
            setSuccess('Resume parsed successfully!')
            setTimeout(() => setSuccess(false), 5000)
            
            // Reset file input to allow uploading the same file again
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
          } else if (status.status === 'error') {
            // Only process error if this is for the job we're currently waiting for
            if (currentJobIdRef.current && status.jobId !== currentJobIdRef.current) {
              return // Continue polling
            }
            
            setParsing(false)
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current)
              pollingIntervalRef.current = null
            }
            currentJobIdRef.current = null // Clear jobId on error
            setError(status.error || 'Failed to parse resume')
            
            // Reset file input to allow uploading again
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
          }
        } catch (err) {
          logger.error('Error polling parse status', {
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
      
      // Poll immediately, then every 10 seconds
      pollStatus()
      pollingIntervalRef.current = setInterval(pollStatus, 10000)
      
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      }
    }
  }, [parsing, getAccessTokenSilently, processProfileData, loadProfileData])

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    // If already parsing, don't start a new upload
    if (parsing) {
      logger.warn('Already parsing a file, ignoring new upload')
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    // Validate file extension first (most reliable)
    const validExtensions = ['.pdf', '.doc', '.docx', '.txt']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
    const isValidExtension = validExtensions.includes(fileExtension)
    
    // Also check MIME type, but be lenient
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain', // TXT files
      'application/zip', // DOCX files are sometimes detected as ZIP
      'application/x-zip-compressed', // Another variant
      'application/octet-stream' // Generic binary
    ]
    const isValidType = !file.type || file.type === '' || validTypes.includes(file.type)
    
    // Accept if extension is valid OR if type is valid (extension check is primary)
    if (!isValidExtension && !isValidType) {
      logger.error('Invalid file uploaded', {
        fileType: file.type,
        fileExtension,
      });
      setError(`Please upload a PDF, DOCX, or TXT file. Detected: ${fileExtension} (${file.type || 'no type'})`)
      // Reset file input after a short delay to allow error to show
      setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }, 100)
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB')
      // Reset file input after a short delay to allow error to show
      setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }, 100)
      return
    }

    // Clear any existing polling interval from previous uploads
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    
    // Set UI feedback immediately - button disabled and "parsing resume..." message
    setError(null)
    setSuccess(false)
    setParseStatus('processing')
    setParsing(true) // This disables the button and shows the parsing message immediately
    currentJobIdRef.current = null // Clear any previous jobId

    try {
      // In test mode, use mock token function
      const tokenFn = isTestMode 
        ? async () => 'mock-test-token'
        : getAccessTokenSilently;
      const response = await parseResume(file, tokenFn)
      
      // Store the jobId for this upload so we only process completion for this specific job
      currentJobIdRef.current = response.jobId
      
      // Parsing is already true, so polling will start via useEffect
      
      // Don't reset input here - let it stay so user can see what was selected
    } catch (err) {
      logger.error('Error uploading resume', {
        error: err instanceof Error ? err.message : String(err),
      });
      Sentry.captureException(err, {
        tags: { component: 'Profile', function: 'handleFileUpload' },
      })
      setParsing(false)
      setParseStatus(null)
      currentJobIdRef.current = null // Clear jobId on error
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload resume'
      setError(errorMessage)
      // Reset file input on error after a delay
      setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }, 100)
    }
  }

  // Pure validation function (doesn't set state)
  const checkExperiencesValid = useCallback((): boolean => {
    return experiences.every((exp) => {
      const hasTitle = exp.title && exp.title.trim()
      const hasCompany = exp.company && exp.company.trim()
      const hasDescription = exp.description && exp.description.trim()
      const hasStartDate = exp.startDate
      const hasEndDate = exp.current || exp.endDate
      
      return hasTitle && hasCompany && hasDescription && hasStartDate && hasEndDate
    })
  }, [experiences])

  // Pure validation function for educations (doesn't set state)
  const checkEducationsValid = useCallback((): boolean => {
    return educations.every((edu) => {
      const hasSchool = edu.school && edu.school.trim()
      const hasDegree = edu.degree && edu.degree.trim()
      const hasField = edu.field && edu.field.trim()
      const hasStartDate = edu.startDate
      const hasEndDate = edu.current || edu.endDate
      
      return hasSchool && hasDegree && hasField && hasStartDate && hasEndDate
    })
  }, [educations])

  const validateExperiences = (showErrors: boolean = true): boolean => {
    const errors: Record<number, Record<string, string>> = {}
    let isValid = true

    experiences.forEach((exp, index) => {
      const expErrors: Record<string, string> = {}

      if (!exp.title || !exp.title.trim()) {
        expErrors.title = 'Job Title is required'
        isValid = false
      }

      if (!exp.company || !exp.company.trim()) {
        expErrors.company = 'Company is required'
        isValid = false
      }

      if (!exp.description || !exp.description.trim()) {
        expErrors.description = 'Description is required'
        isValid = false
      }

      if (!exp.startDate) {
        expErrors.startDate = 'Start Date is required'
        isValid = false
      }

      if (!exp.current && !exp.endDate) {
        expErrors.endDate = 'End Date is required when not currently working there'
        isValid = false
      }

      if (Object.keys(expErrors).length > 0) {
        errors[index] = expErrors
      }
    })

    if (showErrors) {
      setExperienceErrors(errors)
    }
    return isValid
  }

  const validateEducations = (showErrors: boolean = true): boolean => {
    const errors: Record<number, Record<string, string>> = {}
    let isValid = true

    educations.forEach((edu, index) => {
      const eduErrors: Record<string, string> = {}

      if (!edu.school || !edu.school.trim()) {
        eduErrors.school = 'School is required'
        isValid = false
      }

      if (!edu.degree || !edu.degree.trim()) {
        eduErrors.degree = 'Degree is required'
        isValid = false
      }

      if (!edu.field || !edu.field.trim()) {
        eduErrors.field = 'Field of Study is required'
        isValid = false
      }

      if (!edu.startDate) {
        eduErrors.startDate = 'Start Date is required'
        isValid = false
      }

      if (!edu.current && !edu.endDate) {
        eduErrors.endDate = 'End Date is required when not currently enrolled'
        isValid = false
      }

      if (Object.keys(eduErrors).length > 0) {
        errors[index] = eduErrors
      }
    })

    if (showErrors) {
      setEducationErrors(errors)
    }
    return isValid
  }

  // Check if contact info is valid
  const checkContactInfoValid = useCallback((): boolean => {
    const hasName = contactInfo.name && contactInfo.name.trim()
    const hasEmail = contactInfo.email && contactInfo.email.trim()
    return !!(hasName && hasEmail)
  }, [contactInfo])

  // Check if form is valid (for disabling save button)
  const isFormValid = useMemo(() => {
    // Check contact info first (always required)
    const contactInfoValid = checkContactInfoValid()
    
    // If no experiences and no educations, form is valid if contact info is valid
    if (experiences.length === 0 && educations.length === 0) {
      return contactInfoValid
    }
    
    const experiencesValid = experiences.length === 0 || checkExperiencesValid()
    const educationsValid = educations.length === 0 || checkEducationsValid()
    
    return contactInfoValid && experiencesValid && educationsValid
  }, [contactInfo, experiences, educations, checkContactInfoValid, checkExperiencesValid, checkEducationsValid])

  const validateContactInfo = (showErrors: boolean = true): boolean => {
    const errors: Record<string, string> = {}
    let isValid = true

    if (!contactInfo.name || !contactInfo.name.trim()) {
      errors.name = 'Full Name is required'
      isValid = false
    }

    if (!contactInfo.email || !contactInfo.email.trim()) {
      errors.email = 'Email Address is required'
      isValid = false
    } else {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(contactInfo.email.trim())) {
        errors.email = 'Please enter a valid email address'
        isValid = false
      }
    }

    if (showErrors) {
      setContactInfoErrors(errors)
    }
    return isValid
  }

  const handleSave = async () => {
    return Sentry.startSpan(
      {
        op: "ui.click",
        name: "Save Profile",
      },
      async (span) => {
        // Validate contact info, experiences and educations before saving (show errors)
        const contactInfoValid = validateContactInfo(true)
        const experiencesValid = validateExperiences(true)
        const educationsValid = validateEducations(true)
    
        if (!contactInfoValid || !experiencesValid || !educationsValid) {
          const errorMessages = []
          if (!contactInfoValid) {
            errorMessages.push('contact information')
          }
          if (!experiencesValid) {
            errorMessages.push('work experience entries')
          }
          if (!educationsValid) {
            errorMessages.push('education entries')
          }
          setError(`Please fix the validation errors in your ${errorMessages.join(', ')} before saving.`)
          
          // Scroll to first error
          if (!contactInfoValid) {
            const errorElement = document.querySelector('[data-contact-info-section]')
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          } else {
            const firstExpErrorIndex = Object.keys(experienceErrors)[0]
            const firstEduErrorIndex = Object.keys(educationErrors)[0]
            
            if (firstExpErrorIndex) {
              const errorElement = document.querySelector(`[data-experience-index="${firstExpErrorIndex}"]`)
              if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
            } else if (firstEduErrorIndex) {
              const errorElement = document.querySelector(`[data-education-index="${firstEduErrorIndex}"]`)
              if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
            }
          }
          return
        }

        try {
          setSaving(true)
          setError(null)
          setSuccess(false)

          const profileData = {
            aboutMe: aboutMe.trim(),
            experiences,
            educations,
            skills,
            contactInfo
          }

          // In test mode, use mock token function
          const tokenFn = isTestMode 
            ? async () => 'mock-test-token'
            : getAccessTokenSilently;
          await saveProfile(profileData, tokenFn)
          setSuccess('Profile saved successfully!')
          // Clear errors on successful save
          setExperienceErrors({})
          setEducationErrors({})
          setContactInfoErrors({})
          // Navigate to dashboard after a short delay to show success message
          setTimeout(() => {
            navigate('/dashboard')
          }, 1000)
        } catch (err) {
          logger.error('Error saving profile', {
            error: err instanceof Error ? err.message : String(err),
          });
          Sentry.captureException(err, {
            tags: { component: 'Profile', function: 'handleSave' },
          })
          const errorMessage = err instanceof Error ? err.message : 'Failed to save profile'
          setError(errorMessage)
        } finally {
          setSaving(false)
        }
      }
    )
  }

  const addExperience = () => {
    setExperiences([...experiences, {
      id: Date.now(),
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      current: false,
      description: '',
      points: []
    }])
  }

  const updateExperience = (index: number, field: keyof Experience, value: any) => {
    setExperiences(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
    
    // Clear error for this field when user starts typing
    if (experienceErrors[index] && experienceErrors[index][field]) {
      const newErrors = { ...experienceErrors }
      delete newErrors[index][field]
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index]
      }
      setExperienceErrors(newErrors)
    }
  }

  const updateExperienceMultiple = (index: number, updates: Partial<Experience>) => {
    setExperiences(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], ...updates }
      return updated
    })
    
    // Clear errors for updated fields
    if (experienceErrors[index]) {
      const newErrors = { ...experienceErrors }
      Object.keys(updates).forEach(field => {
        if (newErrors[index][field]) {
          delete newErrors[index][field]
        }
      })
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index]
      }
      setExperienceErrors(newErrors)
    }
  }

  const removeExperience = (index: number) => {
    setExperiences(experiences.filter((_, i) => i !== index))
  }

  const addEducation = () => {
    setEducations([...educations, {
      id: Date.now(),
      school: '',
      degree: '',
      field: '',
      startDate: '',
      endDate: '',
      current: false,
      description: ''
    }])
  }

  const updateEducation = (index: number, field: keyof Education, value: any) => {
    setEducations(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
    
    // Clear error for this field when user starts typing
    if (educationErrors[index] && educationErrors[index][field]) {
      const newErrors = { ...educationErrors }
      delete newErrors[index][field]
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index]
      }
      setEducationErrors(newErrors)
    }
  }

  const updateEducationMultiple = (index: number, updates: Partial<Education>) => {
    setEducations(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], ...updates }
      return updated
    })
    
    // Clear errors for updated fields
    if (educationErrors[index]) {
      const newErrors = { ...educationErrors }
      Object.keys(updates).forEach(field => {
        if (newErrors[index][field]) {
          delete newErrors[index][field]
        }
      })
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index]
      }
      setEducationErrors(newErrors)
    }
  }

  const removeEducation = (index: number) => {
    setEducations(educations.filter((_, i) => i !== index))
  }

  const addSkill = () => {
    setShowSkillInput(true)
    setNewSkill('')
    // Focus the input after it's rendered
    setTimeout(() => {
      skillInputRef.current?.focus()
    }, 0)
  }

  const handleAddSkill = () => {
    const trimmedSkill = newSkill.trim()
    if (trimmedSkill && !skills.includes(trimmedSkill)) {
      setSkills([...skills, trimmedSkill])
      setNewSkill('')
      setShowSkillInput(false)
    } else if (skills.includes(trimmedSkill)) {
      // Skill already exists, just close the input
      setNewSkill('')
      setShowSkillInput(false)
    }
  }

  const handleSkillInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddSkill()
    } else if (e.key === 'Escape') {
      setShowSkillInput(false)
      setNewSkill('')
    }
  }

  const cancelAddSkill = () => {
    setShowSkillInput(false)
    setNewSkill('')
  }

  const removeSkill = (index: number) => {
    setSkills(skills.filter((_, i) => i !== index))
  }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end for experiences
  const handleExperienceDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setExperiences((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        const reordered = arrayMove(items, oldIndex, newIndex)
        
        // Reorder errors to match new item positions
        setExperienceErrors((prevErrors) => {
          const newErrors: Record<number, Record<string, string>> = {}
          reordered.forEach((item, newIdx) => {
            const oldIdx = items.findIndex((oldItem) => oldItem.id === item.id)
            if (prevErrors[oldIdx]) {
              newErrors[newIdx] = prevErrors[oldIdx]
            }
          })
          return newErrors
        })
        
        return reordered
      })
    }
  }

  // Handle drag end for educations
  const handleEducationDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setEducations((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        const reordered = arrayMove(items, oldIndex, newIndex)
        
        // Reorder errors to match new item positions
        setEducationErrors((prevErrors) => {
          const newErrors: Record<number, Record<string, string>> = {}
          reordered.forEach((item, newIdx) => {
            const oldIdx = items.findIndex((oldItem) => oldItem.id === item.id)
            if (prevErrors[oldIdx]) {
              newErrors[newIdx] = prevErrors[oldIdx]
            }
          })
          return newErrors
        })
        
        return reordered
      })
    }
  }

  // Handle drag end for skills
  const handleSkillDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setSkills((items) => {
        const oldIndex = Number(active.id)
        const newIndex = Number(over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const isDisabled = parsing || saving

  // In test mode, don't show loading spinner - show content immediately
  if (!isTestMode && (effectiveAuthLoading || loading)) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="profile-container">
      <div className="container py-5">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            <div className="profile-card p-4 p-md-5">
              <div className="profile-header">
                <h1>My Profile</h1>
                <p>Upload your resume (PDF, DOCX, or TXT) or manually enter your information below.</p>
              </div>

              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
              )}

              {success && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  {success}
                  <button type="button" className="btn-close" onClick={() => setSuccess(false)}></button>
                </div>
              )}

              {/* File Upload Section */}
              <div className="form-section mb-4">
                <div className="section-header">
                  <h3>Upload Resume</h3>
                </div>
                <div className="mb-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                    onChange={handleFileUpload}
                    disabled={isDisabled}
                    className="file-input-hidden"
                    id="resume-file-input"
                  />
                  <button
                    type="button"
                    className={`btn btn-sm btn-outline-primary file-upload-button ${isDisabled ? 'disabled' : ''}`}
                    disabled={isDisabled}
                    onClick={(e) => {
                      e.preventDefault()
                      if (!isDisabled && fileInputRef.current) {
                        fileInputRef.current.click()
                      }
                    }}
                  >
                    Choose File
                  </button>
                  <small className="text-muted d-block mt-2">
                    Upload a PDF, DOCX, or TXT file. The system will automatically extract and parse your resume information.
                  </small>
                </div>
                {parsing && (
                  <div className="alert alert-info">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Parsing resume... {parseStatus === 'processing' ? '(processing usually takes 5 minutes)' : parseStatus && `(${parseStatus})`}
                  </div>
                )}
              </div>

              {/* Contact Information Section */}
              <div className="form-section" data-contact-info-section>
                <div className="section-header">
                  <h3>Contact Information</h3>
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${contactInfoErrors.name ? 'is-invalid' : ''}`}
                      placeholder="John Doe"
                      value={contactInfo.name}
                      onChange={(e) => {
                        setContactInfo({ ...contactInfo, name: e.target.value })
                        // Clear error when user starts typing
                        if (contactInfoErrors.name) {
                          const newErrors = { ...contactInfoErrors }
                          delete newErrors.name
                          setContactInfoErrors(newErrors)
                        }
                      }}
                      disabled={isDisabled}
                    />
                    {contactInfoErrors.name && (
                      <div className="invalid-feedback">{contactInfoErrors.name}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-control ${contactInfoErrors.email ? 'is-invalid' : ''}`}
                      placeholder="your.email@example.com"
                      value={contactInfo.email}
                      onChange={(e) => {
                        setContactInfo({ ...contactInfo, email: e.target.value })
                        // Clear error when user starts typing
                        if (contactInfoErrors.email) {
                          const newErrors = { ...contactInfoErrors }
                          delete newErrors.email
                          setContactInfoErrors(newErrors)
                        }
                      }}
                      disabled={isDisabled}
                    />
                    {contactInfoErrors.email && (
                      <div className="invalid-feedback">{contactInfoErrors.email}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Address</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="123 Main St, City, State ZIP"
                      value={contactInfo.address}
                      onChange={(e) => setContactInfo({ ...contactInfo, address: e.target.value })}
                      disabled={isDisabled}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      className="form-control"
                      placeholder="+1 (555) 123-4567"
                      value={contactInfo.phone}
                      onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                      disabled={isDisabled}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">LinkedIn Profile</label>
                    <input
                      type="url"
                      className="form-control"
                      placeholder="https://linkedin.com/in/yourprofile"
                      value={contactInfo.linkedIn}
                      onChange={(e) => setContactInfo({ ...contactInfo, linkedIn: e.target.value })}
                      disabled={isDisabled}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">GitHub Profile</label>
                    <input
                      type="url"
                      className="form-control"
                      placeholder="https://github.com/yourusername"
                      value={contactInfo.github}
                      onChange={(e) => setContactInfo({ ...contactInfo, github: e.target.value })}
                      disabled={isDisabled}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Portfolio URL</label>
                    <input
                      type="url"
                      className="form-control"
                      placeholder="https://yourportfolio.com"
                      value={contactInfo.portfolio}
                      onChange={(e) => setContactInfo({ ...contactInfo, portfolio: e.target.value })}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </div>

              {/* About Me Section */}
              <div className="form-section">
                <div className="section-header">
                  <h3>About Me</h3>
                </div>
                <label htmlFor="about-me" className="form-label">
                  Professional Summary
                </label>
                <textarea
                  id="about-me"
                  className="form-control"
                  rows={4}
                  placeholder="Enter a brief professional summary (2-3 sentences)..."
                  value={aboutMe}
                  onChange={(e) => setAboutMe(e.target.value)}
                  disabled={isDisabled}
                />
              </div>

              {/* Skills Section */}
              <div className="form-section">
                <div className="section-header d-flex justify-content-between align-items-center">
                  <h3>Skills</h3>
                  {!showSkillInput && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={addSkill}
                      disabled={isDisabled}
                    >
                      + Add Skill
                    </button>
                  )}
                </div>
                {skills.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleSkillDragEnd}
                  >
                    <SortableContext
                      items={skills.map((_, index) => index)}
                      strategy={undefined}
                    >
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        {skills.map((skill, index) => (
                          <SortableSkillItem
                            key={index}
                            skill={skill}
                            index={index}
                            isDisabled={isDisabled}
                            removeSkill={removeSkill}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {showSkillInput && (
                      <div className="d-flex align-items-center gap-2 skill-input-wrapper">
                        <input
                          ref={skillInputRef}
                          type="text"
                          className="form-control form-control-sm skill-input"
                          placeholder="Enter skill name"
                          value={newSkill}
                          onChange={(e) => setNewSkill(e.target.value)}
                          onKeyDown={handleSkillInputKeyDown}
                          disabled={isDisabled}
                          autoFocus
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={handleAddSkill}
                          disabled={isDisabled || !newSkill.trim()}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={cancelAddSkill}
                          disabled={isDisabled}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    {skills.length === 0 && !showSkillInput && (
                      <p className="text-muted">No skills added yet. Click "Add Skill" to add one.</p>
                    )}
                  </div>
                )}
                {showSkillInput && skills.length > 0 && (
                  <div className="d-flex align-items-center gap-2 skill-input-wrapper mt-2">
                    <input
                      ref={skillInputRef}
                      type="text"
                      className="form-control form-control-sm skill-input"
                      placeholder="Enter skill name"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      onKeyDown={handleSkillInputKeyDown}
                      disabled={isDisabled}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleAddSkill}
                      disabled={isDisabled || !newSkill.trim()}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={cancelAddSkill}
                      disabled={isDisabled}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Experiences Section */}
              <div className="form-section">
                <div className="section-header d-flex justify-content-between align-items-center">
                  <h3>Work Experience</h3>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={addExperience}
                    disabled={isDisabled}
                  >
                    + Add Experience
                  </button>
                </div>
                {experiences.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleExperienceDragEnd}
                  >
                    <SortableContext
                      items={experiences.map((exp) => exp.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {experiences.map((exp, index) => (
                        <SortableExperienceItem
                          key={exp.id}
                          experience={exp}
                          index={index}
                          experienceErrors={experienceErrors[index] || {}}
                          isDisabled={isDisabled}
                          updateExperience={updateExperience}
                          updateExperienceMultiple={updateExperienceMultiple}
                          removeExperience={removeExperience}
                          setExperienceErrors={setExperienceErrors}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <p className="text-muted">No work experience added yet. Click "Add Experience" to add one.</p>
                )}
              </div>

              {/* Education Section */}
              <div className="form-section">
                <div className="section-header d-flex justify-content-between align-items-center">
                  <h3>Education</h3>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={addEducation}
                    disabled={isDisabled}
                  >
                    + Add Education
                  </button>
                </div>
                {educations.length > 0 ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleEducationDragEnd}
                  >
                    <SortableContext
                      items={educations.map((edu) => edu.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {educations.map((edu, index) => (
                        <SortableEducationItem
                          key={edu.id}
                          education={edu}
                          index={index}
                          educationErrors={educationErrors[index] || {}}
                          isDisabled={isDisabled}
                          updateEducation={updateEducation}
                          updateEducationMultiple={updateEducationMultiple}
                          removeEducation={removeEducation}
                          setEducationErrors={setEducationErrors}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <p className="text-muted">No education added yet. Click "Add Education" to add one.</p>
                )}
              </div>

              {/* Save Button */}
              <div className="action-buttons">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => window.history.back()}
                  disabled={isDisabled}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isDisabled || !isFormValid}
                  title={!isFormValid ? 'Please fill in all required fields (contact information, work experience, and education entries)' : ''}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Profile'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile
